import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Box, Checkbox, Collapse, IconButton, Paper, Radio, Tooltip, Typography } from '@mui/material'
import { MRT_Row, MRT_TableInstance } from 'material-react-table'
import { memo, MouseEvent, ReactNode, SyntheticEvent } from 'react'

import { MrtMobileCardConfig, MrtMobileSummaryField, MrtPath } from './mrtTypes.ts'

export interface MrtMobileCardProps<T extends Record<string, any>> {
  row: T
  mrtRow?: MRT_Row<T>
  config: MrtMobileCardConfig<T>
  table: MRT_TableInstance<T>
  selected: boolean
  selectionEnabled: boolean
  multiSelection: boolean
  expanded: boolean
  onToggleExpand: () => void
  renderRowActions: (params: { row: MRT_Row<T> | { original: T }; table: MRT_TableInstance<T> }) => ReactNode
  renderDetailPanel?: (row: T) => ReactNode
}

export const getNestedValue = (object: unknown, path: string): unknown => {
  if (object === null || object === undefined || !path) {
    return undefined
  }

  return path.split('.').reduce<unknown>((current, key) => {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }

    return (current as Record<string, unknown>)[key]
  }, object)
}

/** Humaniza el label, funciona cuando solo se envia key o el item de array */
const humanizeLabel = (value: string): string => {
  const lastPart = value.split('.').pop() ?? value

  return lastPart.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())
}

const resolveMobileContent = <T extends Record<string, any>>(
  definition: MrtPath<T> | ((row: T) => ReactNode) | undefined,
  row: T,
): ReactNode => {
  if (!definition) {
    return null
  }

  if (typeof definition === 'function') {
    return definition(row)
  }

  const value = getNestedValue(row, definition)

  if (value === null || value === undefined || value === '') {
    return '-'
  }

  return String(value)
}

interface ResolvedMobileField {
  field: string
  label: string
  value: ReactNode
}

const resolveMobileField = <T extends Record<string, any>>(
  field: MrtMobileSummaryField<T>,
  row: T,
): ResolvedMobileField => {
  const isConfig = typeof field === 'object' && field !== null
  const path = String(isConfig ? field.field : field)
  const rawValue = getNestedValue(row, path)
  if (isConfig && typeof field.render === 'function') {
    return {
      field: path,
      label: field.label ?? humanizeLabel(path),
      value: field.render({
        value: rawValue,
        row,
      }),
    }
  }
  return {
    field: path,
    label: isConfig && field.label ? field.label : humanizeLabel(path),
    value: rawValue === null || rawValue === undefined || rawValue === '' ? '-' : String(rawValue),
  }
}

/** Componente principal de la tarjeta para móviles */
function MrtMobileCardComponent<T extends Record<string, any>>({
  row,
  mrtRow,
  config,
  table,
  selected,
  selectionEnabled,
  multiSelection,
  expanded,
  onToggleExpand,
  renderRowActions,
  renderDetailPanel,
}: MrtMobileCardProps<T>) {
  const title = resolveMobileContent(config.title, row)
  const subtitle = resolveMobileContent(config.subtitle, row)
  const summaryFields = config.summaryFields?.map((field) => resolveMobileField(field, row)) ?? []
  const hasDetailPanel = typeof renderDetailPanel === 'function'
  const actionRow = mrtRow ?? {
    original: row,
  }

  // ===== EVENTOS =====
  const handleCardClick = () => {
    if (!selectionEnabled || !mrtRow) {
      return
    }
    mrtRow.toggleSelected()
  }

  const handleSelectionChange = (event: SyntheticEvent) => {
    event.stopPropagation()
    mrtRow?.toggleSelected()
  }

  const handleExpand = (event: MouseEvent) => {
    event.stopPropagation()

    onToggleExpand()
  }

  /* ------------------------------------------------------------------------ */
  /* RENDER                                                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <Paper
      variant="outlined"
      onClick={handleCardClick}
      sx={(theme) => ({
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
        ...(selected && {
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        }),
        '&:hover': {
          borderColor: selected ? theme.palette.primary.main : theme.palette.action.active,
          boxShadow: theme.shadows[1],
        },
      })}
    >
      <Box
        sx={(theme) => ({
          px: 1.25,
          py: 0.65,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: selected ? theme.palette.action.selected : theme.palette.action.hover,
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
        })}
      >
        {selectionEnabled &&
          (multiSelection ? (
            <Checkbox
              size="small"
              checked={selected}
              onChange={handleSelectionChange}
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0.5,
              }}
            />
          ) : (
            <Radio
              size="small"
              checked={selected}
              onChange={handleSelectionChange}
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0.5,
              }}
            />
          ))}
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            noWrap
            sx={{
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle !== null && subtitle !== undefined && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{
                display: 'block',
                mt: 0.1,
                lineHeight: 1.1,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: 0.25,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {renderRowActions({
            row: actionRow,
            table,
          })}

          {hasDetailPanel && (
            <Tooltip title={expanded ? 'Ocultar detalles' : 'Ver detalles'}>
              <IconButton
                size="small"
                onClick={handleExpand}
                aria-label={expanded ? 'Ocultar detalles' : 'Ver detalles'}
                aria-expanded={expanded}
                sx={{
                  ml: 0.25,
                }}
              >
                {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {config.renderMobileSummary ? (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
          }}
        >
          {config.renderMobileSummary(row)}
        </Box>
      ) : (
        summaryFields.length > 0 && (
          <Box
            sx={{
              px: 1.5,
              py: 1.1,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              columnGap: 1.5,
              rowGap: 1.5,
            }}
          >
            {summaryFields.map(({ field, label, value }) => (
              <Box
                key={field}
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: '0.66rem',
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                  }}
                >
                  {label}
                </Typography>

                <Box
                  sx={{
                    mt: 0.25,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    '& > *': {
                      maxWidth: '100%',
                    },
                  }}
                >
                  {typeof value === 'string' ? (
                    <Typography
                      variant="body2"
                      fontWeight={400}
                      noWrap
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {value}
                    </Typography>
                  ) : (
                    value
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )
      )}

      {hasDetailPanel && (
        <Collapse in={expanded} timeout="auto" unmountOnExit onClick={(event) => event.stopPropagation()}>
          <Box
            sx={{
              px: 1.5,
              pb: 1.25,
            }}
          >
            <Box
              sx={{
                pt: 1.25,
                borderTop: '1px dashed',
                borderColor: 'divider',
              }}
            >
              {renderDetailPanel(row)}
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  )
}

/**
 * Render componente para renderizar una fila de la tabla en modo mobile
 * @author isi-template
 */
export const MrtMobileCard = memo(MrtMobileCardComponent) as typeof MrtMobileCardComponent
